import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { GTMTracking, Tappable } from '@common/ui/general';

import {
  paginationSelector,
  requestParamsSelector,
} from '@alumni_v2/selectors/reviews';
import {
  updateRequestParams,
} from '@alumni_v2/stores/reviews/actions';

import { useMediaQuery } from '@common/hooks';


import CloseIcon from '@alumni_v2/images/svg/close.svg';
import ShareProfile from '../ShareProfile/ShareProfile';
import styles from './FilterTags.module.scss';


const GTM_PROPS = {
  gtmEventType: 'cta',
  gtmEventAction: 'click',
  gtmEventResult: 'clear_filter_tag',
  gtmEventCategory: 'alumni_directory',
};

const Filter = ({ label, value, onRemove }) => {
  const onRemoveHandler = () => onRemove(value);

  if (typeof label !== 'string') {
    if (
      typeof label.min !== 'undefined'
      && typeof label.max !== 'undefined'
    ) {
      const text = `${label.min} - ${label.max}`;
      return (
        <Tappable
          onClick={onRemoveHandler}
          className={styles.tag}
          {...GTM_PROPS}
          gtmEventData={{
            click_text: text,
            click_type: 'clear_filter_tag',
          }}
        >
          <span>
            {text}
          </span>
          <img src={CloseIcon} alt="close" />

        </Tappable>
      );
    } else if (typeof label.min !== 'undefined') {
      const text = `${label.min} and above`;

      return (
        <Tappable
          onClick={onRemoveHandler}
          className={styles.tag}
          {...GTM_PROPS}
          gtmEventData={{
            click_text: text,
            click_type: 'clear_filter_tag',
          }}
        >
          <span>
            {text}
          </span>
          <img src={CloseIcon} alt="close" />
        </Tappable>
      );
    }
  }

  return (
    <Tappable
      onClick={onRemoveHandler}
      className={styles.tag}
      {...GTM_PROPS}
      gtmEventData={{
        click_text: label,
        click_type: 'clear_filter_tag',
      }}
    >
      <span>{label}</span>
      <img src={CloseIcon} alt="close" />
    </Tappable>
  );
};

function FilterTags({ quickFilters, showShareProfile = false }) {
  const dispatch = useDispatch();

  const requestParams = useSelector(requestParamsSelector) || {};
  const pagination = useSelector(paginationSelector);

  const { mobile } = useMediaQuery();

  const onAdvancedFilterRemove = useCallback((filterValue) => {
    const { advancedFilters } = requestParams;
    const updatedFilters = advancedFilters[
      filterValue.key
    ].filter(
      item => item !== filterValue.value,
    );

    const updateAdvancedFilters = (myFilters, filterKey, changedFilters) => {
      const newFilters = { ...myFilters };
      if (changedFilters.length > 0) {
        newFilters[filterKey] = changedFilters;
      } else {
        delete newFilters[filterKey];
      }
      return newFilters;
    };

    // eslint-disable-next-line max-len
    const newAdvancedFilters = updateAdvancedFilters(advancedFilters, filterValue?.key, updatedFilters);

    dispatch(
      updateRequestParams(
        [],
        newAdvancedFilters,
        null,
      ),
    );
  }, [dispatch, requestParams]);

  const onQuickFilterRemove = useCallback((value) => {
    const updatedFilters = requestParams.quickFilters.filter(
      item => item !== value,
    );

    dispatch(
      updateRequestParams(
        updatedFilters, {}, null,
      ),
    );
  }, [dispatch, requestParams]);

  const onClearAllFilter = useCallback(() => {
    dispatch(
      updateRequestParams(
        [], {}, null,
      ),
    );
  }, [dispatch]);


  if (
    requestParams.search
    || requestParams.quickFilters.length
    || Object.keys(requestParams.advancedFilters).length
  ) {
    return (
      <div className={styles.container}>
        {!mobile && (
          <div className={styles.title}>
            Filtered Results
            {' '}
            <span>
              (
              {pagination?.totalEntries}
              {' '}
              Alumni)
            </span>
          </div>
        )}
        <div className={styles.allFilterTags}>
          {requestParams.quickFilters.map((item, idx) => {
            const result = quickFilters.filter(qf => qf.value === item)[0];
            return (
              <Filter
                key={idx}
                label={result.label}
                value={result.value}
                onRemove={onQuickFilterRemove}
              />
            );
          })}
          {Object.keys(
            requestParams.advancedFilters,
          ).map(advFilter => requestParams.advancedFilters[advFilter].map(
            (item, idx) => {
              const value = { key: advFilter, value: item };
              return (
                <Filter
                  key={idx}
                  onRemove={onAdvancedFilterRemove}
                  label={item}
                  value={value}
                />
              );
            },
          ))}
          <GTMTracking
            className={
              `${styles.clearBtn}
            ${(Object.keys(requestParams.quickFilters).length === 0
               && Object.keys(requestParams.advancedFilters).length === 0)
              && styles.clearBtnAlt}`
            }
            onClick={onClearAllFilter}
            component="button"
            gtmEventType="cta"
            gtmEventAction="click"
            gtmEventResult="clear_all_filters"
            gtmEventCategory="alumni_directory"
            gtmEventData={{
              click_type: 'clear_all_filters',
              click_section: 'filter_tags',
            }}
          >
            Clear all filters
          </GTMTracking>
          { showShareProfile && <ShareProfile /> }
        </div>
      </div>
    );
  }

  return <></>;
}

export default FilterTags;
